import { IEventLoggingComponentProps, withEventLogging } from '@AppModels/Logging';
import {
	IAccount,
	IMeetingContext,
	INote,
	IOperationResultNoValue,
	IRichContentContext,
	RichContentContextSource,
	createRawRichTextContentStateWithText,
} from '@ViewModels';
import { StyleDeclarationValue, css } from 'aphrodite';
import { action, computed, makeObservable, observable } from 'mobx';
import { inject, observer } from 'mobx-react';
import moment from 'moment';
import * as React from 'react';
import { InternalMeetingNoteType } from '../../../../models/AdminModels';
import {
	ErrorMessagesViewModelKey,
	IErrorMessageComponentProps,
	IUserSessionComponentProps,
	UserSessionViewModelKey,
} from '../../../../models/AppState';
import { getDisplayName } from '../../../../models/NameUtils';
import {
	AccountDetailsNoteViewModel,
	AccountOperationsViewModel,
	AdminPermission,
} from '../../../../viewmodels/AdminViewModels';
import { FeedCardNoteContent } from '../../../../web/components/cards/FeedCardNoteContent';
import { CalendarDateIcon } from '../../../../web/components/svgs/icons/CalendarDateIcon';
import { EditItemPenIcon } from '../../../../web/components/svgs/icons/EditItemPenIcon';
import { NotesIcon } from '../../../../web/components/svgs/icons/NotesIcon';
import { success } from '../../../../web/styles/colors';
import { baseStyleSheet } from '../../../../web/styles/styles';
import { ConditionalAccess } from '../../ConditionalAccess';
import { AccountDetailsNoteComposerModal } from '../AccountDetailsNoteComposer';
import { styleSheet } from './styles';

interface IProps extends IEventLoggingComponentProps, IUserSessionComponentProps, IErrorMessageComponentProps {
	accountOperations: AccountOperationsViewModel;
	className?: string;
	headerActions?: React.ReactNode;
	styles?: StyleDeclarationValue[];
	title?: React.ReactNode;
	type: InternalMeetingNoteType;
}

interface IState {
	isLoading?: boolean;
	noteContentOverride?: string;
}

class _AccountDetailsNote extends React.Component<IProps, IState> {
	private static Messages = {
		clickToAdd: 'Click the edit icon to the right to add a note.',
		errorLoading: 'Error loading note',
		noNote: 'No note',
	};
	public readonly state: IState = {
		isLoading: true,
	};
	private mNote: AccountDetailsNoteViewModel;
	private mPendingNote: AccountDetailsNoteViewModel;

	constructor(props: IProps) {
		super(props);

		makeObservable<
			_AccountDetailsNote,
			'mNote' | 'mPendingNote' | 'shouldShowMissingWarning' | 'onNoteComposerRequestClose'
		>(this, {
			mNote: observable.ref,
			mPendingNote: observable.ref,
			shouldShowMissingWarning: computed,
			onNoteComposerRequestClose: action,
		});
	}

	public componentDidMount() {
		this.loadNote();
	}

	public render() {
		const { className, styles } = this.props;
		return (
			<div
				className={`${css(
					styleSheet.container,
					this.shouldShowMissingWarning ? styleSheet.containerMissingNoteWarning : null,
					...(styles || [])
				)} account-details-note ${className || ''}`}
			>
				{this.renderHeader()}
				{this.renderNoteContent()}
			</div>
		);
	}

	private renderNoteContent() {
		const { type } = this.props;
		const { noteContentOverride } = this.state;
		const bodyText = noteContentOverride || (
			<FeedCardNoteContent
				note={this.mNote}
				onRenderExpansionToggle={this.onRenderShowMoreToggle}
				styles={[styleSheet.bodyText, styleSheet.noteContent]}
			/>
		);
		const meetingContext = this.mNote?.context as IMeetingContext;
		const hasAdditionalMeetingInfo = meetingContext?.name && meetingContext?.meetingStatus;
		return (
			<div className={css(styleSheet.bodyText)}>
				{this.shouldShowMissingWarning || !this.mNote ? (
					<ConditionalAccess
						elseChildren={_AccountDetailsNote.Messages.noNote}
						permission={AdminPermission.AnnotateAccount}
					>
						<span>
							{noteContentOverride ||
								(type === InternalMeetingNoteType.CSM ||
								type === InternalMeetingNoteType.Websites ||
								type === InternalMeetingNoteType.Ads
									? _AccountDetailsNote.Messages.noNote
									: _AccountDetailsNote.Messages.clickToAdd)}
						</span>
					</ConditionalAccess>
				) : (
					<>
						<div className={css(baseStyleSheet.horizontalStack, styleSheet.title, styleSheet.noteInfo)}>
							<span>{getDisplayName(this.mNote.creator)}</span>
							<span>{moment(this.mNote.lastModifiedDateAsDate).format('MMM DD, YYYY hh:mm')}</span>
							{hasAdditionalMeetingInfo && (
								<>
									<span> - </span>
									<span>
										{meetingContext.name} ({meetingContext.meetingStatus})
									</span>
								</>
							)}
						</div>
						{bodyText}
					</>
				)}
			</div>
		);
	}

	private renderHeader() {
		const { headerActions, title, type, accountOperations } = this.props;
		return (
			<div className={css(styleSheet.header)}>
				<div
					className={css(styleSheet.title, this.shouldShowMissingWarning ? styleSheet.titleMissingNoteWarning : null)}
				>
					{title || this.renderDefaultTitle()}
				</div>
				<div className={css(styleSheet.headerButtons)}>
					<ConditionalAccess permission={AdminPermission.AnnotateAccount}>
						{type === InternalMeetingNoteType.CSM ? (
							<div className={css(styleSheet.noteActions)}>
								<button
									className={css(
										baseStyleSheet.horizontalStack,
										baseStyleSheet.brandLink,
										styleSheet.noteActionsButton
									)}
									onClick={this.showNoteComposer(false)}
								>
									<NotesIcon className={css(styleSheet.noteActionsIcon)} fillColor={success} />
									<span>Add a Note</span>
								</button>
								<button
									className={css(
										baseStyleSheet.horizontalStack,
										baseStyleSheet.brandLink,
										styleSheet.noteActionsButton
									)}
									onClick={this.showNoteComposer(false, true)}
								>
									<CalendarDateIcon className={css(styleSheet.noteActionsIcon)} fillColor={success} />
									<span>Add Meeting Notes</span>
								</button>
							</div>
						) : type === InternalMeetingNoteType.Websites || type === InternalMeetingNoteType.Ads ? (
							<div className={css(styleSheet.noteActions)}>
								<button
									className={css(
										baseStyleSheet.horizontalStack,
										baseStyleSheet.brandLink,
										styleSheet.noteActionsButton
									)}
									onClick={this.showNoteComposer(false, true)}
								>
									<CalendarDateIcon className={css(styleSheet.noteActionsIcon)} fillColor={success} />
									<span>Add Meeting Notes</span>
								</button>
							</div>
						) : (
							<button onClick={this.showNoteComposer()}>
								<EditItemPenIcon />
							</button>
						)}
						{!!this.mPendingNote && (
							<AccountDetailsNoteComposerModal
								account={accountOperations.account}
								modalProps={{
									isOpen: !!this.mPendingNote,
									onRequestClose: this.onNoteComposerRequestClose,
								}}
								note={this.mPendingNote}
								onAccountUpdated={this.onAccountUpdated}
								onNextMeetingSelected={this.onNextMeetingSelected}
								type={type}
							/>
						)}
					</ConditionalAccess>
					{headerActions}
				</div>
			</div>
		);
	}

	private renderDefaultTitle() {
		const { type } = this.props;
		switch (type) {
			case InternalMeetingNoteType.CSM: {
				return 'Latest CSM Note';
			}
			case InternalMeetingNoteType.Sales: {
				return this.shouldShowMissingWarning ? 'Sales Notes Missing!' : 'Summary from Sales';
			}
			case InternalMeetingNoteType.Summary: {
				return 'Summary from CSM';
			}
			case InternalMeetingNoteType.Websites: {
				return 'Latest Website Note';
			}
			case InternalMeetingNoteType.Ads: {
				return 'Latest Ads Note';
			}
			default: {
				break;
			}
		}
	}

	private onRenderShowMoreToggle = (isExpanded: boolean, toggle: (expand: boolean) => void) => {
		return (
			<button
				className={css(
					baseStyleSheet.brandLink,
					styleSheet.showMoreLessButton,
					isExpanded ? styleSheet.showLessButton : null
				)}
				onClick={this.onToggleExpandClicked(toggle, !isExpanded)}
			>
				<span>{isExpanded ? 'Show Less' : 'Read More'}</span>
			</button>
		);
	};

	private onToggleExpandClicked = (toggle: (expand: boolean) => void, expand: boolean) => () => {
		toggle(expand);
	};

	private get shouldShowMissingWarning() {
		const { type } = this.props;
		return (type === InternalMeetingNoteType.Sales || type === InternalMeetingNoteType.Summary) && !this.mNote;
	}

	private onNoteComposerRequestClose = (note?: INote, cancel?: boolean) => {
		this.mPendingNote = null;
		if (!cancel && !!note?.id) {
			const { accountOperations, type } = this.props;
			this.mNote = new AccountDetailsNoteViewModel(accountOperations, type, note);
			this.setState({
				noteContentOverride: null,
			});
		}
	};

	private getNoteId = () => {
		const { type, accountOperations } = this.props;

		let noteId: string = null;
		switch (type) {
			case InternalMeetingNoteType.CSM: {
				noteId = accountOperations.account.additionalInfo?.csmNoteId;
				break;
			}
			case InternalMeetingNoteType.Sales: {
				noteId = accountOperations.account.additionalInfo?.salesNoteId;
				break;
			}
			case InternalMeetingNoteType.Summary: {
				noteId = accountOperations.account.additionalInfo?.summaryNoteId;
				break;
			}
			case InternalMeetingNoteType.Websites: {
				noteId = accountOperations.account.additionalInfo?.websitesNoteId;
				break;
			}
			case InternalMeetingNoteType.Ads: {
				noteId = accountOperations.account.additionalInfo?.adsNoteId;
				break;
			}
			default: {
				this.mNote = null;
				break;
			}
		}

		return noteId;
	};

	private loadNote = () => {
		const { type, accountOperations, logApiError } = this.props;

		const noteId = this.getNoteId();
		if (noteId == null) {
			this.mNote = null;
		}

		if (noteId) {
			this.setState({
				noteContentOverride: null,
			});
			this.mNote = new AccountDetailsNoteViewModel(accountOperations, type, {
				id: noteId,
			});
			this.mNote.load()?.catch((error: IOperationResultNoValue) => {
				logApiError('NoteLoad-Error', error);
				this.setState({
					noteContentOverride: `${_AccountDetailsNote.Messages.errorLoading}${
						error.systemCode ? ` (${error.systemCode})` : ''
					}`,
				});

				this.mNote = null;
			});
		}
	};

	private showNoteComposer =
		(copyContentFromExistingNote = true, forMeeting = false) =>
		() => {
			const { accountOperations, type } = this.props;
			let context: IRichContentContext | IMeetingContext = {
				...((copyContentFromExistingNote ? this.mNote?.context : null) || {}),
			} as any;
			if (forMeeting) {
				const meetingContext: IMeetingContext = {
					durationInMinutes: 60,
					meetingStatus: 'Completed',
					...context,
					source: RichContentContextSource.Meeting,
				};
				context = meetingContext;
			}
			const noteId = this.getNoteId();
			this.mPendingNote = new AccountDetailsNoteViewModel(accountOperations, type, {
				actionItems: copyContentFromExistingNote ? this.mNote?.toJs().actionItems || [] : [],
				content:
					!!copyContentFromExistingNote && !!this.mNote
						? this.mNote.toJs().content
						: createRawRichTextContentStateWithText(''),
				context,
				id: noteId,
			});
		};

	private onNextMeetingSelected = (nextMeeting: Date) => {
		const { accountOperations, errorMessages, logApiError, type } = this.props;
		if (type === InternalMeetingNoteType.CSM) {
			accountOperations.updateNextMeeting(nextMeeting)?.catch(error => {
				logApiError('SetNextMeeting-Error', error);

				errorMessages.pushApiError(error);
			});
		} else if (type === InternalMeetingNoteType.Websites) {
			accountOperations.updateNextWebsitesMeeting(nextMeeting)?.catch(error => {
				logApiError('SetNextWebsitesMeeting-Error', error);

				errorMessages.pushApiError(error);
			});
		} else if (type === InternalMeetingNoteType.Ads) {
			accountOperations.updateNextAdsMeeting(nextMeeting)?.catch(error => {
				logApiError('SetNextAdsMeeting-Error', error);

				errorMessages.pushApiError(error);
			});
		} else {
			throw new Error('Invalid type for setting next meeting');
		}
	};

	private onAccountUpdated = (updatedAccount: IAccount) => {
		const { accountOperations } = this.props;
		// Update the account data in MobX so the UI reflects the change
		accountOperations.updateAccountData(updatedAccount);
	};
}

const AccountDetailsNoteAsObserver = observer(_AccountDetailsNote);
const AccountDetailsNoteWithContext = inject(
	UserSessionViewModelKey,
	ErrorMessagesViewModelKey
)(AccountDetailsNoteAsObserver);
export const AccountDetailsNote = withEventLogging(AccountDetailsNoteWithContext, 'AccountDetailsNote');
